import * as React from "react";
import {useMemo, useState} from "react";
import {Badge, Col, Container, Row} from "react-bootstrap";
import {useParams} from "react-router-dom";
import {WASpinner} from "./WASpinner";
import {createLinkToDownload, formatNumber, isUserAdmin, tryCall} from "../utils/commons.ts";
import {KContainer, KErrorPage, TransDate, Translate} from "@kopjra/uikit";
import {useGetSessionQuery, useLazyGetFileQuery} from "../api/session.ts";
import {useGetUserInfo} from "../api/user.ts";
import {Panel, PanelGroup} from "react-resizable-panels";
import {ResizeHandle} from "./resizePanel/ResizeHandle.tsx";

import styles from "./resizePanel/PanelResize.module.scss";
import {FileCategory, SessionStateMap} from "../types/sessionsstate.ts";
import moment from "moment";
import { FilesBrowser } from "./FilesBrowser.tsx";
import {getApiUrl} from "../api";
import {useTranslation} from "react-i18next";
import {Preview} from "./Preview.tsx";
import {BigWaiter} from "./BigWaiter.tsx";

export const DetailBrowser: React.FC = () => {
    const {sessionId} = useParams<{sessionId: string}>();

    const [selected, setSelected] = useState<{link: string, name: string, category: FileCategory} | undefined>(undefined);
    const [previewing, setPreviewing] = useState(false);

    const {data: detail} = useGetSessionQuery({id: sessionId || 0, files: true});
    const {data: userInfo} = useGetUserInfo();

    const [triggerGetFile] = useLazyGetFileQuery();

    const {t} = useTranslation();

    const isAdmin = useMemo(() => isUserAdmin(userInfo), [userInfo]);
    const elapsed = useMemo(() => {
        if (detail && detail.start && detail.end) {
            const duration = moment.duration(moment(detail.end).diff(moment(detail.start)));
            return `${formatNumber(Math.floor(duration.asHours()))}:${formatNumber(duration.minutes())}:${formatNumber(duration.seconds())}`;
        }
    }, [detail]);

    return (
        <>
            <div className="parent">
                {!detail ? (
                    <KContainer>
                        <Row style={{marginTop: 150, marginBottom: 50}}>
                            <Col className="text-center">
                                <BigWaiter/>
                            </Col>
                        </Row>
                    </KContainer>
                ) : (detail && detail.softDeleted && !isAdmin) ? (
                    <KErrorPage title={<Translate i18nKey="error.page"/>} code={"403"} message={<Translate i18nKey="error.forbidden"/>}/>
                ) : (
                    <PanelGroup className={styles.PanelGroup} direction="vertical">
                        <Panel className={styles.PanelRow} minSize={10} collapsible={true}>
                            <PanelGroup className={styles.PanelGroup} direction="horizontal">
                                <Panel className={styles.PanelColumn} defaultSize={25} minSize={10}>
                                    <div className="inner">
                                    <Container>
                                        <Row className="title">
                                            <Col><Translate i18nKey="details.titleSession"/></Col>
                                        </Row>
                                        {detail && detail.softDeleted ? (
                                            <Row style={{marginTop: -20}}>
                                                <Col md={12} className="label"><Badge pill={true} bg="danger"><Translate i18nKey="details.deleted"/></Badge></Col>
                                            </Row>
                                        ) : null}
                                        <Row style={{marginTop: 30}}>
                                            <Col md={4} className="label">ID</Col>
                                            <Col md={8} className="value">{detail && detail.id}</Col>
                                        </Row>
                                        <Row>
                                            <Col md={4} className="label"><Translate i18nKey="details.name"/></Col>
                                            <Col md={8} className="value">{detail && detail.name}</Col>
                                        </Row>
                                        <Row>
                                            <Col md={4} className="label"><Translate i18nKey="details.created"/></Col>
                                            <Col md={8} className="value">{(detail && detail.created) ? <TransDate date={detail.created} dateFormat="long"/> : null}</Col>
                                        </Row>
                                        <Row>
                                            <Col md={4} className="label"><Translate i18nKey="details.state"/></Col>
                                            <Col md={8} className="value">{detail ? <Translate i18nKey={`acquisitions.stateValues.${SessionStateMap[detail.state]}`}/> : null}</Col>
                                        </Row>
                                        <Row>
                                            <Col md={4} className="label"><Translate i18nKey="details.elapsed"/></Col>
                                            <Col md={8} className="value">{elapsed ? elapsed : "--"}</Col>
                                        </Row>
                                    </Container>
                                    </div>
                                </Panel>
                                <ResizeHandle className={styles.ResizeHandle} />
                                <Panel className={styles.PanelColumn} minSize={10}>
                                    <div className="inner">
                                    <Row className="title">
                                        <Col><Translate i18nKey="details.titleFiles"/></Col>
                                    </Row>
                                    <FilesBrowser
                                        files={detail ? detail.files : []}
                                        onDownloadSelected={async (categories) => {
                                            const query: Record<string, string> = {categories: categories.join(",")};
                                            createLinkToDownload(`partial_session_${detail && detail.id}.zip`, await getApiUrl({urlComponent: `/sessions/${detail && detail.id}/files/archive`, query}))
                                        }}
                                        onDownloadSingleFile={async (file) => {
                                            await tryCall(triggerGetFile({ sessionId: detail.id, fileId: file.id }).unwrap(), {
                                                error: t("error.download"),
                                            }, (sessionFile) => {
                                                if (sessionFile?.link) {
                                                    createLinkToDownload(sessionFile.name, sessionFile.link);
                                                }
                                            })
                                        }}
                                        onPreviewFile={async (file) => {
                                            setPreviewing(true);
                                            await tryCall(triggerGetFile({ sessionId: detail.id, fileId: file.id }).unwrap(), {

                                            }, (sessionFile) => {
                                                if (sessionFile?.link) {
                                                    setSelected({link: sessionFile.link, name: sessionFile.name, category: sessionFile.category});
                                                }
                                            });
                                            setPreviewing(false);
                                        }}
                                    />
                                    </div>
                                </Panel>
                            </PanelGroup>
                        </Panel>
                        <ResizeHandle className={styles.ResizeHandle} />
                        <Panel className={styles.PanelRow} defaultSize={60} minSize={20}>
                            <div className="inner preview">
                                {(!previewing && !selected) ? (
                                    <div className="wrapper">
                                        <span className="no-file"><Translate i18nKey="details.previewNoFile"/>&nbsp;&nbsp;&nbsp;<i className={"fal fa-file-arrow-up"}/></span>
                                    </div>
                                ) : null}
                                {(previewing) ? (
                                    <div className="wrapper">
                                        <WASpinner size={200}/>
                                    </div>
                                ) : selected ? (
                                    <Preview link={selected.link} name={selected.name} category={selected.category}/>
                                ) : null}
                            </div>
                        </Panel>
                    </PanelGroup>
                )}
            </div>
        </>
    );
};
